import { Box, Flex, Icon, Text } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import {
  IoCheckmarkCircle,
  IoEye,
  IoInformationCircle,
  IoLockClosed,
  IoWarning,
} from "react-icons/io5";
import type { ReviewState } from "../reviewLogic";
import { STICKY_CHROME_PROPS } from "components/nav/stickyChrome";

/**
 * ReviewStateAlert — the compact review-state banner under the navigator
 * (PLO-4803). A slim status bar: a small filled status icon, a semibold title,
 * then the message inline after it. Success tint for complete/ready, azurite
 * for awaiting, warning for revisions, neutral for the attestor read-only view
 * and for a cycle the admin has ended.
 */

type Tone = "info" | "success" | "warning" | "neutral";

interface AlertConfig {
  tone: Tone;
  icon: IconType;
  title: string;
  /** default message; the `message` prop overrides (e.g. the revision reason) */
  message: string;
}

const CONFIG: Partial<Record<ReviewState, AlertConfig>> = {
  ready_to_submit: {
    tone: "success",
    icon: IoCheckmarkCircle,
    title: "All accounts reviewed",
    message:
      "Every account has a decision. Submit your decisions, or use Edit to make changes first.",
  },
  awaiting_approval: {
    tone: "info",
    icon: IoInformationCircle,
    title: "Awaiting admin approval",
    message:
      "Your review has been submitted and is awaiting approval. You'll be notified if any changes are needed.",
  },
  revision_requested: {
    tone: "warning",
    icon: IoWarning,
    title: "Revision requested",
    message: "An admin has asked for changes before this review can be approved.",
  },
  read_only: {
    tone: "neutral",
    icon: IoEye,
    title: "Read-only view",
    message:
      "You're viewing this review as the assigned attestor. Decisions are read-only. Head back to the attestation page when you're ready to sign.",
  },
  complete: {
    tone: "success",
    icon: IoCheckmarkCircle,
    title: "Review complete",
    message: "This review has been approved. No further action is needed.",
  },
  cycle_ended: {
    tone: "neutral",
    icon: IoLockClosed,
    title: "Review cycle ended",
    message:
      "An admin has ended this review cycle, so its decisions can no longer be made or changed. No further action is needed.",
  },
};

const TONE_STYLES: Record<
  Tone,
  { bg: string; border: string; icon: string; title: string; body: string }
> = {
  info: {
    bg: "bg.azurite",
    border: "p.200",
    icon: "p.500",
    title: "p.700",
    body: "p.600",
  },
  success: {
    bg: "bg.success",
    border: "success.200",
    icon: "success.500",
    title: "success.700",
    body: "success.600",
  },
  warning: {
    bg: "bg.warning",
    border: "warning.200",
    icon: "warning.500",
    title: "warning.700",
    body: "warning.600",
  },
  neutral: {
    bg: "bg.section",
    border: "border.default",
    icon: "text.body.tertiary",
    title: "text.body.primary",
    body: "text.body.tertiary",
  },
};

/** Whether ReviewStateAlert renders a banner for this state (vs. nothing). The
 *  sticky navigator consults this so it doesn't stack a second sticky element
 *  under the shell nav — the alert takes the sticky slot when present. */
export const hasReviewStateBanner = (state: ReviewState): boolean =>
  CONFIG[state] != null;

export interface ReviewStateAlertProps {
  state: ReviewState;
  /** override the default heading (e.g. a set with nothing left in scope) */
  title?: string;
  /** override the default body copy (e.g. the admin's revision message) */
  message?: string;
  /** optional trailing action (e.g. Submit / Edit buttons) */
  action?: React.ReactNode;
}

const ReviewStateAlert = ({
  state,
  title,
  message,
  action,
}: ReviewStateAlertProps) => {
  const config = CONFIG[state];
  if (!config) return null;
  const styles = TONE_STYLES[config.tone];

  return (
    <Box
      position="sticky"
      // Parks below the app's 68px sticky TopNav rather than under it.
      top="68px"
      zIndex={20}
      {...STICKY_CHROME_PROPS}
      width="full"
      alignSelf="stretch"
      bg="bg.page"
      pt="sm"
      pb="md"
    >
      <Flex
        align="center"
        gap="sm"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={styles.border}
        borderRadius="component.lg"
        bg={styles.bg}
        px="md"
        py="sm"
      >
        <Icon as={config.icon} boxSize="16px" color={styles.icon} flexShrink={0} />
        <Text fontSize="smd" color={styles.body} lineHeight="1.4" flex="1" minW={0}>
          <Text as="span" fontWeight={600} color={styles.title}>
            {title ?? config.title}
          </Text>{" "}
          {message ?? config.message}
        </Text>
        {action && <Box flexShrink={0}>{action}</Box>}
      </Flex>
    </Box>
  );
};

export default ReviewStateAlert;
